// frontend/src/components/AnimeCard.jsx
import React, { useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bookmark } from "lucide-react";
import { useAnime } from "../context/AnimeContext";

function AnimeCard({ anime, variant = "trending" }) {
  const { addToFavorites, removeFromFavorites, isAnimeFavorited } = useAnime();
  const navigate = useNavigate();

  // MUST be top-level (not conditional)
  const gestureRef = useRef({ x: 0, y: 0, moved: false, pointerId: null });
  const MOVE_PX = 10;

  const resetGesture = () => {
    gestureRef.current.pointerId = null;
    gestureRef.current.moved = false;
  };

  const onCardPointerDown = (e) => {
    if (e.pointerType === "mouse" && e.button !== 0) return;
    gestureRef.current.x = e.clientX;
    gestureRef.current.y = e.clientY;
    gestureRef.current.moved = false;
    gestureRef.current.pointerId = e.pointerId ?? null;
  };

  const onCardPointerMove = (e) => {
    const g = gestureRef.current;

    // On some mobile browsers pointerId can be flaky during scroll negotiation.
    // If we have a stored pointerId AND this event has one, only then enforce matching.
    if (g.pointerId != null && e.pointerId != null && g.pointerId !== e.pointerId)
      return;

    const dx = Math.abs(e.clientX - g.x);
    const dy = Math.abs(e.clientY - g.y);

    if (dx > MOVE_PX || dy > MOVE_PX) g.moved = true;
  };

  // NOTE: We don't navigate in pointerup anymore (mobile can cancel/alter pointer sequences).
  // Navigation is handled by onClick for reliability.
  const onCardPointerUp = () => {
    resetGesture();
  };

  const onCardPointerCancel = () => {
    resetGesture();
  };

  const onCardKeyDown = (animeId) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      navigate(`/anime/${animeId}`);
    }
  };

  const onCardClick = (animeId) => (e) => {
    // If a child (like the favorite button) prevented/handled it, bail.
    if (e.defaultPrevented) return;

    // If user swiped/scroll-moved, do NOT navigate
    if (gestureRef.current.moved) return;

    navigate(`/anime/${animeId}`);
  };

  if (!anime) return null;

  // Normalize ID to ONE consistent type (string)
  const rawId =
    anime.AnimeID ??
    anime.anime_id ??
    anime.id ??
    anime.AnimeId ??
    anime.animeId;

  if (rawId === undefined || rawId === null) return null;

  const animeId = String(rawId);

  const isFavorite =
    typeof isAnimeFavorited === "function" ? isAnimeFavorited(animeId) : false;

  const handleFavoriteClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (isFavorite) {
        await removeFromFavorites(animeId);
      } else {
        await addToFavorites({
          ...anime,
          AnimeID: animeId,
          anime_id: animeId,
          id: animeId,
        });
      }
    } catch (error) {
      console.error("Favorite toggle error:", error);
    }
  };

  // ---------- Safe normalizers ----------
  const safeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") {
      try {
        const parsed = JSON.parse(val);
        if (Array.isArray(parsed)) return parsed;
      } catch {}
      if (val.includes(","))
        return val
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
      return [val].filter(Boolean);
    }
    return [];
  };

  const Genres = safeArray(anime.Genres);

  const title = anime.Title || anime.title || "Untitled";
  const studio = anime.Studio || anime.studio || "";
  const statusRaw = anime.Status || anime.status || "";
  const statusLabel =
    statusRaw === "RELEASING"
      ? "AIRING"
      : statusRaw === "FINISHED"
      ? "FINISHED"
      : statusRaw;

  const descriptionRaw = anime.Description || anime.description || "";
  const descriptionText =
    typeof descriptionRaw === "string" && descriptionRaw.trim()
      ? descriptionRaw.length > 160
        ? `${descriptionRaw.substring(0, 160)}...`
        : descriptionRaw
      : "No description available.";

  const statusBadgeCls =
    statusRaw === "RELEASING"
      ? "border-emerald-400/30 bg-emerald-400/15 text-emerald-200"
      : statusRaw === "FINISHED"
      ? "border-cyan-300/25 bg-cyan-300/12 text-cyan-200"
      : "border-white/15 bg-white/5 text-white/70";

  // ---------- Image ----------
  const sanitizedTitle = String(title)
    .toLowerCase()
    .replace(/[^a-z0-9]/gi, "")
    .trim();
  const fallbackImage = sanitizedTitle ? `/images/${sanitizedTitle}.jpg` : null;

  const getImageSrc = () => {
    const url = anime.PhotoUrl || anime.photo_url || anime.image;
    if (!url || typeof url !== "string" || url.includes("example.com"))
      return fallbackImage;
    if (url.startsWith("http")) return url;
    return url.startsWith("/") ? url : `/${url.replace(/^\/+/, "")}`;
  };

  const imageSrc = getImageSrc();

  // Ratings (shared)
  const anilistRatingRaw =
    anime.AniListRating ?? anime.anilist_rating ?? anime.rating_anilist ?? null;
  const communityRatingRaw =
    anime.AverageRating ?? anime.average_rating ?? anime.rating_house ?? null;

  const anilistNum =
    anilistRatingRaw !== "" &&
    anilistRatingRaw !== null &&
    anilistRatingRaw !== undefined
      ? Number(anilistRatingRaw)
      : null;

  const communityNum =
    communityRatingRaw !== "" &&
    communityRatingRaw !== null &&
    communityRatingRaw !== undefined
      ? Number(communityRatingRaw)
      : null;

  const anilistText =
    anilistNum !== null && !Number.isNaN(anilistNum)
      ? anilistNum.toFixed(1)
      : "—";
  const communityText =
    communityNum !== null && !Number.isNaN(communityNum)
      ? communityNum.toFixed(1)
      : "—";

  // Trending overlay variant (mobile scroll-safe tap)
  if (variant === "trendingOverlay") {
    const ratingPillBase =
      "inline-flex items-center gap-2 rounded-full border border-white/12 bg-black/45 px-2.5 py-1";

    return (
      <div
        role="link"
        tabIndex={0}
        aria-label={`Open ${title}`}
        onKeyDown={onCardKeyDown(animeId)}
        onClick={onCardClick(animeId)}
        onPointerDown={onCardPointerDown}
        onPointerMove={onCardPointerMove}
        onPointerUp={onCardPointerUp}
        onPointerCancel={onCardPointerCancel}
        className={[
          "group relative block w-full overflow-hidden rounded-2xl",
          "border border-white/10 bg-black/20",
          "hover:bg-white/[0.03] hover:border-white/20 transition",
          "focus:outline-none focus:ring-2 focus:ring-emerald-300/25",
          "select-none cursor-pointer",
          // better mobile tap behavior (still allows scrolling)
          "touch-manipulation",
        ].join(" ")}
        style={{ textDecoration: "none" }}
        data-no-drag="true"
      >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-white/[0.03]">
          {/* subtle blurred backdrop so contain still feels premium */}
          {imageSrc ? (
            <img
              src={imageSrc}
              alt=""
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 h-full w-full object-cover scale-110 blur-2xl opacity-25"
              loading="lazy"
              decoding="async"
            />
          ) : null}

          {/* Actual poster (FULL, no crop) */}
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={`Poster of ${title}`}
              className="absolute inset-0 h-full w-full object-contain transition-transform duration-500 group-hover:scale-[1.01]"
              loading="lazy"
              decoding="async"
              sizes="(max-width: 640px) 58vw, (max-width: 1024px) 28vw, 220px"
              onError={(e) => {
                if (
                  fallbackImage &&
                  e.currentTarget.src !== window.location.origin + fallbackImage
                ) {
                  e.currentTarget.src = fallbackImage;
                }
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-white/10" />
          )}

          {/* Top-left status badge */}
          {statusRaw ? (
            <div
              className={[
                "absolute top-2 left-2",
                "px-2 py-0.5 rounded-full",
                "text-[9px] font-semibold tracking-wide",
                "border bg-black/35 backdrop-blur-md",
                statusBadgeCls,
              ].join(" ")}
            >
              {statusLabel}
            </div>
          ) : null}

          {/* Favorite button (top-right) */}
          <button
            type="button"
            onClick={handleFavoriteClick}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            className={`
              absolute top-2 right-2
              inline-flex items-center justify-center
              h-7 w-7 sm:h-9 sm:w-9 rounded-xl
              border border-white/10
              bg-black/45 hover:bg-black/60 active:scale-95
              backdrop-blur-md
              transition
              ${
                isFavorite
                  ? "text-pink-400"
                  : "text-white/70 hover:text-emerald-200"
              }
            `}
            title={isFavorite ? "Remove from Favorites" : "Add to Favorites"}
            aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
            data-no-drag="true"
          >
            <Bookmark
              className="w-5 h-5"
              fill={isFavorite ? "currentColor" : "none"}
            />
          </button>

          {/* Bottom fade */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 sm:h-24 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />

          {/* Bottom info strip */}
          <div
            className={[
              "absolute inset-x-0 bottom-0",
              "bg-gradient-to-t from-[#0b0f14]/95 via-[#0b0f14]/80 to-[#0b0f14]/55",
              "backdrop-blur-md",
              "border-t border-white/6",
              "shadow-[0_-8px_24px_rgba(0,0,0,0.22)]",
              "p-3",
            ].join(" ")}
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-3">
              <div className="min-w-0 flex-1">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p
                      className="text-[13px] font-bold tracking-[0.08em] text-emerald-300 line-clamp-1"
                      title={title}
                    >
                      {String(title || "").toUpperCase()}
                    </p>

                    {studio ? (
                      <p className="mt-0.5 text-[10px] text-white/60 line-clamp-1">
                        by {studio}
                      </p>
                    ) : null}
                  </div>

                  <div className="hidden sm:flex flex-col items-end gap-1.5 shrink-0 pt-0.5">
                    <div className={ratingPillBase}>
                      <span className="text-yellow-300 font-extrabold text-[11px] leading-none">
                        {anilistText}
                      </span>
                      <span className="text-white/60 text-[10px] font-semibold leading-none">
                        AniList
                      </span>
                    </div>

                    <div className={ratingPillBase}>
                      <span className="text-emerald-300 font-extrabold text-[11px] leading-none">
                        {communityText}
                      </span>
                      <span className="text-white/60 text-[10px] font-semibold leading-none">
                        AnimeH
                      </span>
                    </div>
                  </div>
                </div>

                <div className="sm:hidden flex items-center gap-2 pt-1">
                  <div className={ratingPillBase}>
                    <span className="text-yellow-300 font-extrabold text-[11px] leading-none">
                      {anilistText}
                    </span>
                    <span className="text-white/60 text-[10px] font-semibold leading-none">
                      AL
                    </span>
                  </div>

                  <div className={ratingPillBase}>
                    <span className="text-emerald-300 font-extrabold text-[11px] leading-none">
                      {communityText}
                    </span>
                    <span className="text-white/60 text-[10px] font-semibold leading-none">
                      AH
                    </span>
                  </div>
                </div>

                <p className="text-[11px] text-white/78 leading-snug line-clamp-3 sm:mt-1 sm:line-clamp-2">
                  {descriptionText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Genre variant (image + title only)
  if (variant === "genre") {
    return (
      <Link
        to={`/anime/${animeId}`}
        className={[
          "group relative block w-full overflow-hidden rounded-2xl",
          "border border-white/10 bg-black/20",
          "hover:bg-white/[0.03] hover:border-white/20 transition",
          "focus:outline-none focus:ring-2 focus:ring-emerald-300/25",
          "active:scale-[0.995]",
          "select-none",
        ].join(" ")}
        style={{ textDecoration: "none" }}
        data-no-drag="true"
      >
        <div className="relative aspect-[3/4] w-full overflow-hidden bg-white/[0.03]">
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={`Poster of ${title}`}
              className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
              loading="lazy"
              decoding="async"
              sizes="(max-width: 640px) 48vw, (max-width: 1024px) 28vw, 220px"
              onError={(e) => {
                if (
                  fallbackImage &&
                  e.currentTarget.src !== window.location.origin + fallbackImage
                ) {
                  e.currentTarget.src = fallbackImage;
                }
              }}
            />
          ) : (
            <div className="absolute inset-0 bg-white/10" />
          )}

          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/75 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-3">
            <p
              className="text-[13px] font-semibold text-white/90 line-clamp-2"
              title={title}
            >
              {title}
            </p>
          </div>
        </div>
      </Link>
    );
  }

  return null;
}

export default AnimeCard;
